import React, { useState, useEffect } from "react";
import { siteSettings } from "../../services/api";
import { showToast } from "../../assets/util/showToast";
import { API_BASE_URL } from "../../config";

interface Setting {
  id?: number;
  setting_key: string;
  setting_value: string;
  setting_type: "text" | "textarea" | "number" | "boolean" | "json";
  label: string;
  description?: string;
  group_name?: string;
}

export default function SiteSettings() {
  const [allSettings, setAllSettings] = useState<Setting[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
  const [editingSettings, setEditingSettings] = useState<
    Record<string, string>
  >({});
  const [groups, setGroups] = useState<string[]>([]);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response: any = await siteSettings.getAll();
      if (response?.data) {
        // Convert response to array format
        const settingsArray: Setting[] = [];
        const groupsSet = new Set<string>();

        // If response is an object with settings
        if (
          typeof response.data === "object" &&
          !Array.isArray(response.data)
        ) {
          // This is the key-value format from the API
          // We need to fetch with more info - let's use a direct API call
          const fullResponse = await fetch(
            API_BASE_URL + "/site-settings.php?action=all"
          ).then((res) => res.json());

          if (fullResponse?.data && Array.isArray(fullResponse.data)) {
            fullResponse.data.forEach((setting: any) => {
              settingsArray.push(setting);
              if (setting.group_name) {
                groupsSet.add(setting.group_name);
              }
            });
          } else {
            // Fallback: parse from key-value response
            Object.entries(response.data).forEach(
              ([key, value]: [string, any]) => {
                settingsArray.push({
                  setting_key: key,
                  setting_value: String(value),
                  setting_type: "text",
                  label: key.replace(/_/g, " ").toUpperCase(),
                  group_name: "Custom",
                });
                groupsSet.add("Custom");
              }
            );
          }
        }

        setAllSettings(settingsArray);
        const groupsArray = Array.from(groupsSet).sort();
        setGroups(groupsArray);
        if (groupsArray.length > 0) {
          setSelectedGroup(groupsArray[0]);
        }

        // Initialize editing state
        const editState: Record<string, string> = {};
        settingsArray.forEach((setting) => {
          editState[setting.setting_key] = setting.setting_value;
        });
        setEditingSettings(editState);
      }
    } catch (error) {
      console.error("Error loading settings:", error);
      showToast("Error loading settings", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleSetting = (key: string, value: string) => {
    setEditingSettings((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const saveSetting = async (setting: Setting) => {
    try {
      setSaving(true);
      const newValue = editingSettings[setting.setting_key];

      await siteSettings.update(setting.setting_key, newValue);
      showToast(`${setting.label} updated successfully`, "success");

      // Update the allSettings array
      setAllSettings((prev) =>
        prev.map((s) =>
          s.setting_key === setting.setting_key
            ? { ...s, setting_value: newValue }
            : s
        )
      );
    } catch (error) {
      console.error("Error saving setting:", error);
      showToast("Error saving setting", "error");
    } finally {
      setSaving(false);
    }
  };

  const filteredSettings = selectedGroup
    ? allSettings.filter((s) => s.group_name === selectedGroup)
    : allSettings;

  const renderInput = (setting: Setting) => {
    const value = editingSettings[setting.setting_key] || "";

    switch (setting.setting_type) {
      case "textarea":
        return (
          <textarea
            value={value}
            onChange={(e) => handleSetting(setting.setting_key, e.target.value)}
            className="form-control"
            rows={4}
          />
        );
      case "number":
        return (
          <input
            type="number"
            value={value}
            onChange={(e) => handleSetting(setting.setting_key, e.target.value)}
            className="form-control"
          />
        );
      case "boolean":
        return (
          <input
            type="checkbox"
            checked={value === "1" || value === "true"}
            onChange={(e) =>
              handleSetting(setting.setting_key, e.target.checked ? "1" : "0")
            }
            className="form-check-input"
          />
        );
      case "json":
        return (
          <textarea
            value={value}
            onChange={(e) => handleSetting(setting.setting_key, e.target.value)}
            className="form-control font-monospace"
            rows={6}
            placeholder="Enter valid JSON"
          />
        );
      case "text":
      default:
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleSetting(setting.setting_key, e.target.value)}
            className="form-control"
          />
        );
    }
  };

  if (loading) {
    return (
      <div className="page-layout">
        <div className="page-header">
          <h2 className="title">Site Settings</h2>
        </div>
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="page-layout">
      <div className="page-header">
        <h2 className="title">Site Settings</h2>
        <div className="right">
          <button onClick={loadSettings} className="btn btn-secondary">
            <i className="fa-solid fa-refresh"></i> Reload
          </button>
        </div>
      </div>

      <div className="settings-container mt-4">
        {/* Groups Navigation */}
        <div className="settings-groups">
          <div className="groups-label">Groups</div>
          <div className="groups-list">
            {groups.map((group) => (
              <button
                key={group}
                className={`group-btn ${
                  selectedGroup === group ? "active" : ""
                }`}
                onClick={() => setSelectedGroup(group)}
              >
                {group}
              </button>
            ))}
          </div>
        </div>

        {/* Settings Form */}
        <div className="settings-form">
          {filteredSettings.length === 0 ? (
            <div className="no-settings">
              <p>No settings found in this group</p>
            </div>
          ) : (
            <div className="settings-list">
              {filteredSettings.map((setting) => (
                <div key={setting.setting_key} className="setting-item">
                  <div className="setting-header">
                    <label className="setting-label">{setting.label}</label>
                    {setting.description && (
                      <p className="setting-description">
                        {setting.description}
                      </p>
                    )}
                  </div>

                  <div className="setting-input-wrapper">
                    {renderInput(setting)}
                    {setting.setting_type === "boolean" && (
                      <label className="setting-toggle-label">
                        {editingSettings[setting.setting_key] === "1" ||
                        editingSettings[setting.setting_key] === "true"
                          ? "Enabled"
                          : "Disabled"}
                      </label>
                    )}
                  </div>

                  <div className="setting-footer">
                    <span className="setting-key">
                      Key: {setting.setting_key}
                    </span>
                    <button
                      onClick={() => saveSetting(setting)}
                      disabled={
                        saving ||
                        editingSettings[setting.setting_key] ===
                          setting.setting_value
                      }
                      className="btn btn-primary btn-sm"
                    >
                      <i className="fa-solid fa-save"></i> Save
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
